<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import WhatsappRecontactAudience from './WhatsappRecontactAudience.vue';
import WhatsappRecontactClockField from './WhatsappRecontactClockField.vue';
import WhatsappRecontactTemplate from './WhatsappRecontactTemplate.vue';
import {
  fromDatetimeLocal,
  padTime,
  parseTime,
  toDatetimeLocal,
} from '../helpers/whatsappRecontactTime';

const props = defineProps({
  timeZone: { type: String, default: 'UTC' },
  templates: { type: Array, default: () => [] },
  schedule: { type: Object, default: null },
});

const emit = defineEmits(['submit']);
const { t } = useI18n();

const kind = ref('recurring');
const enabled = ref(true);
const templateKey = ref('');
const sendWday = ref('1');
const sendTime = ref('09:00');
const startWday = ref('1');
const startTime = ref('00:00');
const endWday = ref('1');
const endTime = ref('09:00');
const sendAt = ref('');
const windowStartsAt = ref('');
const windowEndsAt = ref('');
const requireAssignee = ref(true);
const requireClosedWindow = ref(true);
const requireUnanswered = ref(true);
const eligibleStatuses = ref(['open', 'pending', 'snoozed']);
const errorMessage = ref('');
const templateRef = ref(null);

const kindOptions = computed(() => [
  { value: 'recurring', label: t('INBOX_MGMT.RECONTACT.KIND.RECURRING') },
  { value: 'one_shot', label: t('INBOX_MGMT.RECONTACT.KIND.ONE_SHOT') },
]);
const templateOptions = computed(() =>
  props.templates.map(template => ({
    value: `${template.name}::${template.language}`,
    label: `${template.name} (${template.language})`,
  }))
);
const selectedTemplate = computed(() =>
  props.templates.find(
    template => `${template.name}::${template.language}` === templateKey.value
  )
);
const isMarketing = computed(
  () =>
    String(selectedTemplate.value?.category || '').toUpperCase() === 'MARKETING'
);
const isRecurring = computed(() => kind.value === 'recurring');

const applySchedule = schedule => {
  kind.value = schedule?.schedule_kind || 'recurring';
  enabled.value = schedule?.enabled !== false;
  const params = schedule?.template_params;
  templateKey.value = params?.name ? `${params.name}::${params.language}` : '';
  sendWday.value = String(schedule?.send_wday ?? 1);
  sendTime.value = padTime(
    schedule?.send_hour ?? 9,
    schedule?.send_minute ?? 0
  );
  startWday.value = String(schedule?.window_start_wday ?? 1);
  startTime.value = padTime(
    schedule?.window_start_hour ?? 0,
    schedule?.window_start_minute ?? 0
  );
  endWday.value = String(schedule?.window_end_wday ?? 1);
  endTime.value = padTime(
    schedule?.window_end_hour ?? 9,
    schedule?.window_end_minute ?? 0
  );
  requireAssignee.value = schedule?.require_assignee !== false;
  requireClosedWindow.value = schedule?.require_closed_window !== false;
  requireUnanswered.value = schedule?.require_unanswered !== false;
  eligibleStatuses.value = schedule?.eligible_statuses?.length
    ? [...schedule.eligible_statuses]
    : ['open', 'pending', 'snoozed'];
  sendAt.value = toDatetimeLocal(schedule?.send_at, props.timeZone);
  windowStartsAt.value = toDatetimeLocal(
    schedule?.window_starts_at,
    props.timeZone
  );
  windowEndsAt.value = toDatetimeLocal(
    schedule?.window_ends_at,
    props.timeZone
  );
  errorMessage.value = '';
};

watch(() => props.schedule, applySchedule, { immediate: true });

const savedProcessedParams = computed(() => {
  const saved = props.schedule?.template_params;
  if (
    !saved?.name ||
    `${saved.name}::${saved.language}` !== templateKey.value
  ) {
    return null;
  }
  return saved.processed_params || null;
});

const audiencePayload = () => ({
  require_assignee: requireAssignee.value,
  require_closed_window: requireClosedWindow.value,
  require_unanswered: requireUnanswered.value,
  eligible_statuses: eligibleStatuses.value,
});

const templateParams = () => ({
  name: selectedTemplate.value.name,
  language: selectedTemplate.value.language,
  processed_params: templateRef.value?.processedParams || {},
});

const clockPayload = (prefix, weekday, time) => {
  const parsed = parseTime(time);
  return {
    [`${prefix}_wday`]: Number(weekday),
    [`${prefix}_hour`]: parsed?.hour ?? null,
    [`${prefix}_minute`]: parsed?.minute ?? null,
  };
};

const recurringPayload = () => ({
  enabled: enabled.value,
  schedule_kind: 'recurring',
  ...clockPayload('send', sendWday.value, sendTime.value),
  ...clockPayload('window_start', startWday.value, startTime.value),
  ...clockPayload('window_end', endWday.value, endTime.value),
  ...audiencePayload(),
  template_params: templateParams(),
});

const oneShotPayload = () => ({
  enabled: enabled.value,
  schedule_kind: 'one_shot',
  send_at: fromDatetimeLocal(sendAt.value, props.timeZone),
  window_starts_at: fromDatetimeLocal(windowStartsAt.value, props.timeZone),
  window_ends_at: fromDatetimeLocal(windowEndsAt.value, props.timeZone),
  ...audiencePayload(),
  template_params: templateParams(),
});

const invalidClock = payload =>
  [payload.send_hour, payload.window_start_hour, payload.window_end_hour].some(
    value => value === null
  );

const submit = () => {
  if (!eligibleStatuses.value.length) {
    errorMessage.value = t('INBOX_MGMT.RECONTACT.AUDIENCE.STATUSES_REQUIRED');
    return;
  }
  if (!selectedTemplate.value) {
    errorMessage.value = t('INBOX_MGMT.RECONTACT.FORM.TEMPLATE_REQUIRED');
    return;
  }
  if (templateRef.value?.isFormInvalid) {
    errorMessage.value = t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE');
    return;
  }
  const payload = isRecurring.value ? recurringPayload() : oneShotPayload();
  payload.headerFile = templateRef.value?.headerFile || null;
  if (isRecurring.value && invalidClock(payload)) {
    errorMessage.value = t('INBOX_MGMT.RECONTACT.FORM.CLOCK_INVALID');
    return;
  }
  if (
    !isRecurring.value &&
    (!payload.send_at || !payload.window_starts_at || !payload.window_ends_at)
  ) {
    errorMessage.value = t('INBOX_MGMT.RECONTACT.FORM.DATE_REQUIRED');
    return;
  }
  if (
    !isRecurring.value &&
    payload.window_ends_at <= payload.window_starts_at
  ) {
    errorMessage.value = t('INBOX_MGMT.RECONTACT.FORM.WINDOW_ORDER');
    return;
  }
  errorMessage.value = '';
  emit('submit', payload);
};

defineExpose({ submit, errorMessage });
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.RECONTACT.FORM.TIMEZONE', { timeZone }) }}
    </p>
    <SettingsToggleSection
      v-model="enabled"
      :header="t('INBOX_MGMT.RECONTACT.FORM.ENABLED')"
      :description="t('INBOX_MGMT.RECONTACT.FORM.ENABLED_DESC')"
    />
    <Select
      v-model="kind"
      accent="brand"
      :options="kindOptions"
      :aria-label="t('INBOX_MGMT.RECONTACT.FORM.KIND')"
    />
    <div v-if="isRecurring" class="flex flex-col gap-4">
      <WhatsappRecontactClockField
        v-model:weekday="sendWday"
        v-model:time="sendTime"
        :label="t('INBOX_MGMT.RECONTACT.FORM.SEND')"
      />
      <WhatsappRecontactClockField
        v-model:weekday="startWday"
        v-model:time="startTime"
        :label="t('INBOX_MGMT.RECONTACT.FORM.WINDOW_START')"
      />
      <WhatsappRecontactClockField
        v-model:weekday="endWday"
        v-model:time="endTime"
        :label="t('INBOX_MGMT.RECONTACT.FORM.WINDOW_END')"
      />
    </div>
    <div v-else class="flex flex-col gap-4">
      <Input
        v-model="sendAt"
        type="datetime-local"
        :label="t('INBOX_MGMT.RECONTACT.FORM.SEND')"
      />
      <Input
        v-model="windowStartsAt"
        type="datetime-local"
        :label="t('INBOX_MGMT.RECONTACT.FORM.WINDOW_START')"
      />
      <Input
        v-model="windowEndsAt"
        type="datetime-local"
        :label="t('INBOX_MGMT.RECONTACT.FORM.WINDOW_END')"
      />
    </div>
    <Select
      v-model="templateKey"
      accent="brand"
      block
      :options="templateOptions"
      :placeholder="t('INBOX_MGMT.RECONTACT.FORM.TEMPLATE')"
      :aria-label="t('INBOX_MGMT.RECONTACT.FORM.TEMPLATE')"
    />
    <p v-if="!templates.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.RECONTACT.FORM.NO_TEMPLATES') }}
    </p>
    <p v-if="isMarketing" class="mb-0 text-sm text-n-amber-11">
      {{ t('INBOX_MGMT.RECONTACT.FORM.MARKETING') }}
    </p>
    <WhatsappRecontactTemplate
      v-if="selectedTemplate?.components"
      ref="templateRef"
      :template="selectedTemplate"
      :saved-params="savedProcessedParams"
      :preview-url="schedule?.header_media_preview_url || ''"
    />
    <WhatsappRecontactAudience
      v-model:require-assignee="requireAssignee"
      v-model:require-closed-window="requireClosedWindow"
      v-model:require-unanswered="requireUnanswered"
      v-model:eligible-statuses="eligibleStatuses"
    />
  </div>
</template>
